import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';

// API/Components
import {
	ANALYSIS_RESULT,
	GRAPH_BOUNDS,
	validValue,
	AnalysisResultToStateColor,
	measUnitsConversion,
	getUserTempUnits,
	getMeasurementAnalysis,
} from '../enums';
import { DrawVerticalDashedLineAtTooltip, UpdateChartData } from '../chart_helpers';
import ResetZoomButton from '../reset_zoom_button';
import CustomGraphLegend from '../custom_graph_legend';

// Material UI
import Grid from '@mui/material/Grid';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';

// Chart.JS
import {
	Chart as ChartJS,
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	Tooltip as ChartJSTooltip,
	BarController,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import zoomPlugin from 'chartjs-plugin-zoom';
import tinycolor from "tinycolor2";

// Register Chart.JS Plugins
ChartJS.register(
	LinearScale,
	CategoryScale,
	BarElement,
	Legend,
	ChartJSTooltip,
	BarController,
	zoomPlugin,
);

// Helper function to sort temperature datasets in proper order
function sortTemperatureItems(a, b, getLabel) {
	// Get labels using the provided getter function
	const aLabel = getLabel(a);
	const bLabel = getLabel(b);
	
	// Define sort order: Temperature, Ambient Temperature, Critical High, Critical Low
	const itemOrder = {
		'Temperature': 0,  // Matches labels that start with "Temperature ("
		'Ambient Temperature': 1,
		'Critical High': 2,
		'Critical Low': 3,
	};
	
	// Get order values, checking for Temperature label variations
	let aOrder = 999;
	let bOrder = 999;
	
	if (aLabel) {
		if (aLabel.startsWith('Temperature')) {
			aOrder = itemOrder['Temperature'];  // Handles "Temperature (°C)" or "Temperature (°F)"
		} else {
			aOrder = itemOrder[aLabel] ?? 999;
		}
	}
	
	if (bLabel) {
		if (bLabel.startsWith('Temperature')) {
			bOrder = itemOrder['Temperature'];  // Handles "Temperature (°C)" or "Temperature (°F)"
		} else {
			bOrder = itemOrder[bLabel] ?? 999;
		}
	}
	
	return aOrder - bOrder;
}

// Temperature Realtime Graph Component
export default function TemperatureRealtimeGraph(props) {

	const {
		realtimeData,
		sysLimitData,
		sysGraphScales,
		graphScales,
		selectedString,
		hasValidTempSensorData,
		hasValidUnitTempData,
		loading
	} = props;

	const [graphData, setGraphData] = useState({});
	const [hasSensors, setHasSensors] = useState(false);
	const [hasUnitTemps, setHasUnitTemps] = useState(false);

	const [graphSettings, setGraphSettings] = useState({
		limitsEnabled: true,
		ambientTempEnabled: true,
		unitTempsView: false,
	});
	const [graphOptions, setGraphOptions] = useState({});

	const chartRef = useRef(null);
	const hasAutoInitializedRef = useRef(false);
	const initializedRef = useRef(false);

	// Helper function to check if temperature sensor data exists
	const checkHasSensorData = (temperatures) => {
		return temperatures && temperatures.length > 0 && temperatures.some(sensor => 
			sensor.temperature !== null && sensor.temperature !== undefined
		);
	};

	// Helper function to check if unit temperature data exists
	const checkHasUnitTempData = (units) => {
		return units && units.length > 0 && units.some(unit => 
			unit.temperature !== null && unit.temperature !== undefined
		);
	};

	// Get the active graph scales based on current data source configuration
	const getActiveGraphScales = () => {
		return graphSettings.unitTempsView ? graphScales : sysGraphScales;
	};

	// Initialize chart options once on first load
	useEffect(() => {
		if (initializedRef.current) { return; }
		
		const activeScales = getActiveGraphScales();
		if (!activeScales) { return; }

		const { tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax } = getAxisRanges(activeScales);
		const options = createChartOptions(tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax);
		setGraphOptions(options);
		
		initializedRef.current = true;
	}, [graphScales, sysGraphScales, graphSettings.unitTempsView]);

	// Detect available data sources
	useEffect(() => {
		if (!hasValidTempSensorData && !hasValidUnitTempData) { return; }

		const sensorsExist = checkHasSensorData(realtimeData?.temperatures);
		const unitTempsExist = checkHasUnitTempData(realtimeData?.units);
		
		setHasSensors(sensorsExist);
		setHasUnitTemps(unitTempsExist);
	}, [realtimeData?.temperatures, realtimeData?.units, hasValidTempSensorData, hasValidUnitTempData]);

	// Initialize and validate the active view
	useEffect(() => {
		if (!hasAutoInitializedRef.current) {
			// First load: determine default view (prioritize unit temps)
			if (hasUnitTemps) {
				setGraphSettings(prev => ({ ...prev, unitTempsView: true }));
				hasAutoInitializedRef.current = true;
			} else if (hasSensors) {
				setGraphSettings(prev => ({ ...prev, unitTempsView: false }));
				hasAutoInitializedRef.current = true;
			} else {
				// No data sources available
				console.error('No temperature data sources available to initially display on the graph; Investigate data availability.');
			}
		} else {
			// After initialization, validate current view is still available
			if (graphSettings.unitTempsView && !hasUnitTemps && hasSensors) {
				setGraphSettings(prev => ({ ...prev, unitTempsView: false }));
			} else if (!graphSettings.unitTempsView && !hasSensors && hasUnitTemps) {
				setGraphSettings(prev => ({ ...prev, unitTempsView: true }));
			} else {
				// No data sources available
				console.error('No temperature data sources available to display on the graph; Investigate data availability.');
			}
		}
	}, [hasSensors, hasUnitTemps, graphSettings.unitTempsView]);

	// Get active data source based on current view
	const activeRealtimeData = useMemo(() => {
		if (!realtimeData || Object.keys(realtimeData).length === 0 || (!hasValidTempSensorData && !hasValidUnitTempData)) {
			return [];
		}

		return graphSettings.unitTempsView ? realtimeData?.units : realtimeData?.temperatures;
	}, [graphSettings.unitTempsView, realtimeData?.units, realtimeData?.temperatures, hasValidTempSensorData, hasValidUnitTempData]);

	// Filter data based on selected string
	const filteredData = useMemo(() => {
		if (!activeRealtimeData || activeRealtimeData.length === 0 || (!hasValidTempSensorData && !hasValidUnitTempData)) {
			return [];
		}

		// If no string selected, return all data
		if (selectedString === 0 || selectedString === null || selectedString === undefined) {
			return activeRealtimeData;
		}

		if (graphSettings.unitTempsView) {
			// Units view: filter by string_id
			return activeRealtimeData.filter(unit => unit.string_id === selectedString);
		} else {
			// Sensors view: only filter if sensors are mapped to units
			const tempSensorsMapped = Boolean(realtimeData?.system?.temp_sensors_mapped);
			if (tempSensorsMapped) {
				// Filter sensors by temp_sensor_id from units in selected string
				const unitsInString = realtimeData?.units?.filter(unit => unit.string_id === selectedString) || [];
				const sensorIds = [...new Set(unitsInString.map(unit => unit.temp_sensor_id).filter(id => id !== null && id !== undefined))];
				return activeRealtimeData.filter(sensor => sensorIds.includes(sensor.sensor_id));
			} else {
				// Sensors not mapped: return all sensors regardless of string selection
				return activeRealtimeData;
			}
		}
	}, [activeRealtimeData, hasValidTempSensorData, hasValidUnitTempData, selectedString, graphSettings.unitTempsView, realtimeData?.units, realtimeData?.system?.temp_sensors_mapped]);

	// Update chart with new data - update data in place or set new graph data for initial load and re-render
	const updateChart = useCallback((datasets, labels) => {
		if (chartRef.current && chartRef.current.data) {
			// Update existing chart (preserves zoom/pan)
			UpdateChartData(chartRef, datasets, labels);
		} else {
			// Initial load: set graph data state
			setGraphData({ labels, datasets });
		}
	}, []);

	// Generate complete graph title with string selection
	const graphTitle = useMemo(() => {
		// Base title
		const baseTitle = graphSettings.unitTempsView ? 'Unit Temperatures' : 'Temperature Sensors';
		
		// Check if string is selected
		const stringSelected = selectedString !== null && selectedString !== undefined && selectedString > 0;
		if (!stringSelected) return baseTitle;
		
		// For unit temps, always show string number
		if (graphSettings.unitTempsView && hasUnitTemps) {
			return `${baseTitle} (String ${selectedString})`;
		}
		
		// For sensors, only show string number if sensors are mapped
		if (!graphSettings.unitTempsView && hasSensors) {
			const tempSensorsMapped = Boolean(realtimeData?.system?.temp_sensors_mapped);
			return tempSensorsMapped ? `${baseTitle} (String ${selectedString})` : baseTitle;
		}
		
		return baseTitle;
	}, [selectedString, graphSettings.unitTempsView, hasUnitTemps, hasSensors, realtimeData?.system?.temp_sensors_mapped]);

	// Process and render temperature data
	useEffect(() => {
		// Early exit if required data is missing
		if (!sysLimitData || sysLimitData.length === 0 || (!hasValidTempSensorData && !hasValidUnitTempData)) {
			return;
		}

		// Early exit if no data sources available
		if (!hasSensors && !hasUnitTemps) {
			setGraphData({ labels: [], datasets: [] });
			return;
		}

		// Early exit if filtered data is empty
		if (!filteredData || filteredData.length === 0) {
			setGraphData({ labels: [], datasets: [] });
			return;
		}

		// Process temperature data with active configuration
		const processedData = processTemperatureData(filteredData, sysLimitData, realtimeData?.system?.ambient_temp);
		
		// Build chart datasets
		const datasets = buildTemperatureDatasets(processedData, sysLimitData);

		// Generate labels
		const dataLabels = graphSettings.unitTempsView
			? filteredData.map(s => `S${s.string_id} U${s.unit_id}`)
			: filteredData.map(s => `Sensor ${s.sensor_id}`);

		// Update chart
		updateChart(datasets, dataLabels);
	}, [filteredData, sysLimitData, realtimeData?.system?.ambient_temp, graphSettings, hasSensors, hasUnitTemps]);

	// Process raw temperature data with unit conversion and analysis
	function processTemperatureData(measurements, limitData, ambientTemp) {
		const temperatures = measurements.map(m => {
			const temp = validValue(m.temperature, null);
			return temp !== null ? measUnitsConversion(temp) : null;
		});
		
		const analyses = measurements.map(m => getMeasurementAnalysis(m.temperature, limitData.hi_crit, limitData.lo_crit, null, null));

		const convertedAmbient = (ambientTemp !== null && ambientTemp !== undefined) ? measUnitsConversion(ambientTemp) : null;

		return {
			temperatures,
			analyses,
			ambientTemperature: convertedAmbient,
			sensorCount: measurements.length,
		};
	}

	// Build all chart datasets from processed data
	function buildTemperatureDatasets(processedData, limitData) {
		const { temperatures, analyses, ambientTemperature, sensorCount } = processedData;
		
		// Start with temperature bars
		let datasets = createTemperatureDataset(temperatures, analyses);
		
		// Add ambient line if available
		if (ambientTemperature !== null) {
			datasets = addAmbientLine(datasets, ambientTemperature, graphSettings.ambientTempEnabled, sensorCount);
		}
		
		// Add limit lines if enabled
		datasets = addLimitDatasets(datasets, limitData, graphSettings.limitsEnabled, sensorCount);

		return datasets;
	}

	// Calculate axis ranges for temperature based on data and extremes
	function getAxisRanges(graphScales) {

		// Initial display range uses graphScales if available, otherwise GRAPH_BOUNDS defaults
		const tempDisplayMax = measUnitsConversion(validValue(graphScales?.temperature?.max, GRAPH_BOUNDS.TEMPERATURE.default_max));
		const tempDisplayMin = measUnitsConversion(validValue(graphScales?.temperature?.min, GRAPH_BOUNDS.TEMPERATURE.default_min));

		// Pan/zoom limits always use GRAPH_BOUNDS bounds
		const tempBoundsMax = measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_max);
		const tempBoundsMin = measUnitsConversion(GRAPH_BOUNDS.TEMPERATURE.bounds_min);

		return { tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax };
	};

	// Create temperature bar dataset with analysis-based colors
	function createTemperatureDataset(temperatures, analyses) {
		const temperatureDataset = {
			label: `Temperature (${getUserTempUnits()})`,
			data: temperatures,
			backgroundColor: temperatures.map((t, idx) => {
				const analysisColor = AnalysisResultToStateColor(analyses[idx].result);
				return tinycolor(analysisColor).setAlpha(0.7).toRgbString();
			}),
			borderColor: temperatures.map((t, idx) => {
				const analysisColor = AnalysisResultToStateColor(analyses[idx].result);
				return tinycolor(analysisColor).darken(15).toHexString();
			}),
			borderWidth: 2,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 1,
		};

		return [temperatureDataset];
	};

	// Create a limit line dataset
	function createLimitLine(label, value, color, sensorCount) {
		const convertedValue = measUnitsConversion(value);
		return {
			label,
			data: Array(sensorCount).fill(convertedValue),
			type: 'line',
			borderColor: color,
			backgroundColor: tinycolor(color).setAlpha(0.7).toRgbString(),
			borderWidth: 2,
			borderDash: [5, 5],
			pointRadius: 0,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 0,
		};
	}

	// Add limit line datasets if enabled and limit data exists
	function addLimitDatasets(datasets, limitData, limitsEnabled, sensorCount) {
		if (!limitsEnabled || !limitData) {
			return datasets;
		}

		// Get critical limit colors
		const highCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_ABOVE_LIMITS_CRITICAL);
		const lowCritColor = AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_BELOW_LIMITS_CRITICAL);

		// Add high critical limit if available
		if (limitData.hi_crit !== undefined && limitData.hi_crit !== null) {
			datasets.push(createLimitLine('Crit High', limitData.hi_crit, highCritColor, sensorCount));
		}

		// Add low critical limit if available
		if (limitData.lo_crit !== undefined && limitData.lo_crit !== null) {
			datasets.push(createLimitLine('Crit Low', limitData.lo_crit, lowCritColor, sensorCount));
		}

		return datasets;
	};

	// Add ambient temperature line
	function addAmbientLine(datasets, ambientTemp, ambientEnabled, sensorCount) {
		if (!ambientEnabled) {
			return datasets;
		}
		
		datasets.push({
			label: 'Ambient Temperature',
			data: Array(sensorCount).fill(ambientTemp),
			type: 'line',
			borderColor: 'rgb(35, 141, 193)', // Blue color for ambient
			backgroundColor: 'rgba(35, 141, 193, 0.7)',
			borderWidth: 3,
			pointRadius: 0,
			yAxisID: 'y',
			xAxisID: 'x',
			order: 2,
		});

		return datasets;
	};

	// Create chart options configuration
	function createChartOptions(tempDisplayMin, tempDisplayMax, tempBoundsMin, tempBoundsMax) {
		return {
			maintainAspectRatio: false,
			animation: false,
			responsive: true,
			interaction: {
				mode: 'index',
				intersect: false,
			},
			scales: {
				x: {
					grid: {
						display: false,
					},
				},
				'y': {
					type: 'linear',
					position: 'left',
					min: tempDisplayMin,
					max: tempDisplayMax,
					title: {
						display: true,
						text: `Temperature (${getUserTempUnits()})`,
						font: {
							size: 14,
							weight: 'bold',
						},
					},
					grid: {
						color: 'rgba(0, 0, 0, 0.1)',
					},
				},
			},
			plugins: {
				legend: {
					display: false,
				},
				tooltip: {
					filter: function(tooltipItem) {
						// Only show tooltip items that have actual values (not null)
						return tooltipItem.parsed.y !== null && tooltipItem.parsed.y !== undefined;
					},
					itemSort: (a, b) => sortTemperatureItems(a, b, (item) => item.dataset.label),
					callbacks: {
						beforeLabel: function(context) {
							// Check if this is the first limit item (after temperature)
							const currentIsLimit = !context.dataset.label?.includes('Temperature');
							
							if (currentIsLimit && context.datasetIndex > 0) {
								// Find the actual previous item in the sorted tooltip
								const tooltipItems = context.chart.tooltip.dataPoints;
								const currentIndex = tooltipItems.findIndex(item => 
									item.datasetIndex === context.datasetIndex
								);
								
								if (currentIndex > 0) {
									const prevItem = tooltipItems[currentIndex - 1];
									const prevItemIsTemperature = prevItem.dataset.label?.includes('Temperature');
									
									if (prevItemIsTemperature) {
										return '\n─── Limits ───';
									}
								}
							}
							return null;
						},
						label: function(context) {
							const datasetLabel = context.dataset.label || '';
							const displayValue = context.parsed.y;

							// Format temperature with appropriate unit
							return `${datasetLabel}: ${displayValue.toFixed(1)} ${getUserTempUnits()}`;
						},
					},
				},
				zoom: {
					pan: {
						enabled: true,
						mode: 'xy',
					},
					limits: {
						'y': {
							min: tempBoundsMin,
							max: tempBoundsMax,
							minRange: 5,
						},
					},
					zoom: {
						mode: 'y',
						wheel: {
							enabled: true,
							speed: 0.05,
						},
						pinch: {
							enabled: true,
						},
						onZoomStart: (e) => {
							if (typeof e.chart.config?.options?.plugins?.zoom?.zoom === "undefined") {
								return;
							}
							if (e.event.shiftKey === true) {
								e.chart.config.options.plugins.zoom.zoom.mode = "x";
								e.chart.config.options.plugins.zoom.zoom.scaleMode = "x";
							} else {
								e.chart.config.options.plugins.zoom.zoom.mode = "y";
								e.chart.config.options.plugins.zoom.zoom.scaleMode = "y";
							}
						},
					},
				},
			},
		};
	};
	
	// Toggle limit markers on the graph
	const handleLimitMarkersToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			limitsEnabled: !prev.limitsEnabled,
		}));
	};

	// Toggle ambient temperature line
	const handleAmbientTempToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			ambientTempEnabled: !prev.ambientTempEnabled,
		}));
	};

	// Toggle between system and unit data sources
	const handleSensorViewToggle = () => {
		setGraphSettings(prev => ({
			...prev,
			unitTempsView: !prev.unitTempsView
		}));
	};
	
	const handleGraphResetZoom = () => {
		if (chartRef.current) {
			chartRef.current.resetZoom();
		}
	};

	return (
		<div
			style={{
				width: '100%',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
				paddingLeft: '5px',
				paddingRight: '5px',
				paddingBottom: '3px',
				position: 'relative',
				overflow: 'hidden',
			}}
		>
			<div style={{ fontSize: '16px', fontWeight: 'bold', textAlign: 'center', flexShrink: 0 }}>
				<strong>{graphTitle}</strong>
			</div>
			<div style={{ 
				flex: '1 1 auto',
				width: '100%',
				minHeight: 0,
				overflow: 'hidden',
			}}>
				{ ((hasValidTempSensorData || hasValidUnitTempData) && graphData && graphData.datasets && graphData.datasets.length > 0) ? (
					<Chart
						ref={chartRef}
						type='bar'
						options={graphOptions}
						data={graphData}
						plugins={[{ afterDraw: chart => DrawVerticalDashedLineAtTooltip(chart) }]}
					/>
				) : (
					<div style={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						height: '100%',
						color: '#666',
					}}>
						{loading ? 'Loading...' : 'No data available'}
					</div>
				)}
			</div>

			<Grid container spacing={1} alignItems="center">
				<Grid item>
					<ToggleButtonGroup
						value={[]}
						onChange={() => {}}
						aria-label="graph markers shown"
						size='small'
						sx={{ padding: '2px' }}
					>
						<Tooltip title="Toggle limit markers on the graph" arrow>
							<ToggleButton
								value="limitMarkers"
								size="small"
								selected={graphSettings.limitsEnabled}
								onClick={handleLimitMarkersToggle}
								sx={{
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Limits
							</ToggleButton>
						</Tooltip>
						<Tooltip title="Toggle ambient temperature line" arrow>
							<ToggleButton
								value="ambientTempLine"
								size="small"
								selected={graphSettings.ambientTempEnabled}
								onClick={handleAmbientTempToggle}
								sx={{
									padding: '2px 8px',
									fontSize: '12px',
									minWidth: 'auto',
								}}
							>
								Ambient
							</ToggleButton>
						</Tooltip>
						{ (hasSensors && hasUnitTemps) && (
							<Tooltip title="Toggle between temperature sensor and unit views" arrow>
								<ToggleButton
									value="sensorView"
									size="small"
									selected={graphSettings.unitTempsView}
									onClick={handleSensorViewToggle}
									sx={{ 
										padding: '2px 8px',
										fontSize: '12px',
										minWidth: 'auto',
									}}
								>
									Units
								</ToggleButton>
							</Tooltip>
						)}
					</ToggleButtonGroup>
				</Grid>
				<Grid item xs sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', rowGap: 0.5, columnGap: 2, padding: '4px 0' }}>
					<CustomGraphLegend
						datasets={graphData.datasets}
						sortFunction={sortTemperatureItems}
						customColorForArrays={AnalysisResultToStateColor(ANALYSIS_RESULT.ANALYSIS_WITHIN_LIMITS)}
					/>
				</Grid>
				<Grid item>
					<ResetZoomButton onClick={handleGraphResetZoom} />
				</Grid>
			</Grid>
		</div>
	);
}